import { useId } from 'react';
import type { LayoutPage } from './layout';
import { REPORT_FONT_FAMILY, reportFontAscent } from './fonts';

export interface ReportCanvasProps {
  page: LayoutPage;
  companyLogo?: string;
}

function chartDescription(chart: NonNullable<LayoutPage['charts']>[number]) {
  if (chart.kind === 'positioning')
    return chart.items
      .map(
        (item) =>
          `${item.label}: price / quality ${item.priceQuality || 'not assessed'}; geographic scope ${item.geoScope || 'not assessed'}; market share ${item.marketSize === null ? 'not assessed' : `${item.marketSize}%`}`
      )
      .join('. ');
  if (chart.kind === 'radar')
    return chart.series
      .map(
        (series) =>
          `${series.label}: ${chart.axes.map((axis, index) => `${axis} ${series.values[index] === null ? 'not assessed' : series.values[index]}`).join('; ')}`
      )
      .join('. ');
  if (chart.kind === 'comparison')
    return chart.items
      .map((item) => `${item.label}: ${item.positive} positive, ${item.negative} negative`)
      .join('; ');
  return chart.items
    .map((item) => `${item.label}: ${item.value === null ? 'not assessed' : item.value}`)
    .join('; ');
}

/** The same positioned scene is used by native PDF and editable PowerPoint. */
export default function ReportCanvas({ page, companyLogo }: ReportCanvasProps) {
  const descriptionId = useId();
  const positionedText = page.elements
    .filter((element) => element.kind === 'text')
    .map((element) => (element.kind === 'text' ? element.text : ''))
    .join(' ');
  const chartData = (page.charts || []).map(chartDescription).filter(Boolean).join('. ');
  return (
    <svg
      viewBox={`0 0 ${page.width} ${page.height}`}
      width={page.width}
      height={page.height}
      role="img"
      aria-label={`${page.title}, page ${page.pageNumber}`}
      aria-describedby={descriptionId}
      className="report-canvas"
      style={{
        display: 'block',
        width: '100%',
        height: 'auto',
        background: '#ffffff',
        fontKerning: 'none',
        fontVariantLigatures: 'none',
      }}
    >
      <title>{page.title}</title>
      <desc
        id={descriptionId}
      >{`Page ${page.pageNumber}. ${positionedText}${chartData ? `. Chart data: ${chartData}.` : ''}`}</desc>
      <rect width={page.width} height={page.height} fill="#ffffff" />
      {page.elements.map((element, index) => {
        switch (element.kind) {
          case 'rect':
            return (
              <rect
                key={index}
                x={element.x}
                y={element.y}
                width={element.width}
                height={element.height}
                rx={element.radius || 0}
                fill={element.fill}
                stroke={element.stroke}
                strokeWidth={element.stroke ? 0.8 : 0}
              />
            );
          case 'line':
            return (
              <line
                key={index}
                x1={element.x}
                y1={element.y}
                x2={element.x2}
                y2={element.y2}
                stroke={element.color}
                strokeWidth={element.width}
              />
            );
          case 'ellipse':
            return (
              <ellipse
                key={index}
                cx={element.x + element.width / 2}
                cy={element.y + element.height / 2}
                rx={element.width / 2}
                ry={element.height / 2}
                fill={element.fill}
                stroke={element.stroke}
                strokeWidth={element.strokeWidth || 0.8}
              />
            );
          case 'image':
            return (
              <image
                key={index}
                href={companyLogo || element.src}
                x={element.x}
                y={element.y}
                width={element.width}
                height={element.height}
                preserveAspectRatio="xMidYMid meet"
              >
                <title>{element.alt}</title>
              </image>
            );
          case 'text': {
            const x =
              element.align === 'right'
                ? element.x + element.width
                : element.align === 'center'
                  ? element.x + element.width / 2
                  : element.x;
            return (
              <text
                key={index}
                x={x}
                y={element.y + reportFontAscent(element.fontSize, element.fontStyle)}
                fill={element.color}
                fontSize={element.fontSize}
                fontFamily={`${REPORT_FONT_FAMILY}, Arial, sans-serif`}
                fontWeight={element.fontStyle.includes('bold') ? 700 : 400}
                fontStyle={element.fontStyle.includes('italic') ? 'italic' : 'normal'}
                textAnchor={
                  element.align === 'right'
                    ? 'end'
                    : element.align === 'center'
                      ? 'middle'
                      : 'start'
                }
                xmlSpace="preserve"
                style={{ userSelect: 'text' }}
              >
                {element.text}
              </text>
            );
          }
        }
      })}
    </svg>
  );
}
